import * as React from "react";
import { Outlet, useLocation } from "react-router-dom";
import { Drawer, PageTransition } from "@mira/ui";
import { DashboardErrorBoundary } from "@/components/error-boundary";
import { OfflineBanner } from "@/components/offline-banner";
import { Sidebar } from "./sidebar";
import { TopNav } from "./top-nav";
import { MobileNav } from "./mobile-nav";
import { supabase } from "@/lib/supabase";
import { isDemoMode } from "@/lib/demo";
import { DEFAULT_AGENT_INSTRUCTIONS } from "@mira/types";
import { useAgentSelectionStore, type DashboardAgent } from "@/stores/agent-selection";

const PAGE_TITLES: Record<string, string> = {
  "/dashboard/home": "Home",
  "/dashboard/persona": "Persona",
  "/dashboard/test": "Test your clone",
  "/dashboard/publish": "Publish & share",
  "/dashboard/visitors": "Visitors",
  "/dashboard/settings": "Settings",
};

export function RootLayout() {
  const [sidebarCollapsed, setSidebarCollapsed] = React.useState(false);
  const [mobileDrawerOpen, setMobileDrawerOpen] = React.useState(false);
  const [isDesktop, setIsDesktop] = React.useState(
    () => window.innerWidth >= 1024,
  );
  const location = useLocation();
  const setAgents = useAgentSelectionStore((s) => s.setAgents);

  const pageTitle = PAGE_TITLES[location.pathname] ?? "Dashboard";
  const sidebarWidth = isDesktop ? (sidebarCollapsed ? 72 : 260) : 0;

  React.useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth >= 1024);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  React.useEffect(() => {
    async function loadAgents() {
      if (isDemoMode) {
        setAgents([
          {
            id: "demo-agent",
            name: "Default Agent",
            status: "live",
            persona: "friendly",
            language: "en",
            custom_instructions: DEFAULT_AGENT_INSTRUCTIONS,
            voice_name: "Aoede",
            embed_enabled: true,
            allowed_origins: ["https://northstar.example"],
            daily_message_quota: 200,
          },
        ]);
        return;
      }

      const { data } = await supabase
        .from("agents")
        .select("id,name,status,persona,language,custom_instructions,voice_name,embed_enabled,allowed_origins,daily_message_quota,created_at")
        .order("created_at", { ascending: true });
      setAgents((data || []) as DashboardAgent[]);
    }

    void loadAgents();
  }, [setAgents]);

  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "b") {
        e.preventDefault();
        if (isDesktop) {
          setSidebarCollapsed((prev) => !prev);
        } else {
          setMobileDrawerOpen((prev) => !prev);
        }
      }
      if (e.key === "Escape") {
        setMobileDrawerOpen(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isDesktop]);

  React.useEffect(() => {
    setMobileDrawerOpen(false);
  }, [location.pathname]);

  return (
    <DashboardErrorBoundary>
      <div className="min-h-screen bg-warm-cream dark:bg-dark-ground transition-colors duration-200">
        {/* Desktop Sidebar */}
        <aside
          className="hidden lg:block fixed left-0 top-0 h-full z-40"
          style={{ width: sidebarWidth }}
        >
          <Sidebar
            collapsed={sidebarCollapsed}
            onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
          />
        </aside>

        {/* Mobile Drawer Sidebar */}
        <Drawer
          isOpen={mobileDrawerOpen}
          onClose={() => setMobileDrawerOpen(false)}
          side="left"
        >
          <Sidebar
            collapsed={false}
            onToggle={() => setMobileDrawerOpen(false)}
          />
        </Drawer>

        {/* Mobile Top Navigation */}
        <header className="lg:hidden fixed top-0 left-0 right-0 z-30 transition-[left] duration-200 ease-out">
          <TopNav
            pageTitle={pageTitle}
            onMenuClick={() => setMobileDrawerOpen(true)}
          />
        </header>

        {/* Main Content */}
        <main
          className="min-h-screen pt-[64px] lg:pt-0 pb-24 lg:pb-12 px-4 sm:px-6 lg:px-12 transition-[margin-left] duration-200 ease-out"
          style={{ marginLeft: sidebarWidth }}
        >
          <div className="w-full py-12">
            <PageTransition>
              <Outlet />
            </PageTransition>
          </div>
        </main>

        {/* Mobile Bottom Navigation */}
        <MobileNav />

        {/* Offline Banner */}
        <OfflineBanner sidebarCollapsed={sidebarCollapsed} />
      </div>
    </DashboardErrorBoundary>
  );
}
